// ──────────────────────────────────────────────────────────────────────────────
// THE COMBINED CONSENT BOX — TERMS, PRIVACY, E-SIGN — ON THE FINAL STEP.
//
// 🚨 THE BOX NO LONGER OPENS ANYTHING. TICKING IT JUST TICKS IT.
// Jesse, 1 Oct 2026: "I really do not like how when you check the consent
// button the PDF of the e-sign consent pops up. Looks like it's broken or
// something weird is happening. Remove the opener for now and simply let them
// check the box and click on the links to open and read."
//
// ⚠️ WHAT THE "SOMETHING WEIRD" ALMOST CERTAINLY WAS, and it is worth recording
// because it was not the pop-up alone. The old design opened the PDF on the
// first tap and then ticked the box when the viewer CLOSED — detected from
// three signals: a window.closed poll, `visibilitychange` back to visible, and
// Capacitor's appStateChange. Two of those fire for reasons that have nothing
// to do with the document: a PDF that DOWNLOADS instead of displaying never
// produces a close, so `awaitingReturn` stayed armed and the NEXT tab switch or
// app resume — minutes later, anywhere in the form — ticked the box by itself.
// A control that ticks itself at a moment the user did not choose is
// indistinguishable from a broken one. All of that machinery is gone.
//
// 🔑 THE §101(c)(1)(C)(ii) ELEMENT IS UNCHANGED AND STILL SERVER-SIDE, and it
// had to be: utils/accountConsent.js REFUSES to record a consent with no logged
// open, and account_consents' CHECK refuses it underneath. E-Sign
// §101(c)(1)(C)(ii) asks for consent given in a manner reasonably demonstrating
// the consumer can ACCESS the electronic form, and an unopened document
// demonstrates nothing. So the open is still logged — BY THE LINK, which is the
// route Jesse named. Tapping "E-Sign Consent" reads it and logs it.
//
// ⚠️ WHICH LEAVES ONE REACHABLE DEAD END, AND IT IS HANDLED AT THE BOX. A
// borrower can tick without ever tapping the link; the server then refuses at
// submit, and ApplicationForm surfaces that message and stops. Correct, and
// late — so the hint below says it AT the box, as soon as the box is ticked
// with no open on record. The rule is the server's; this is where they learn it.
// ──────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState, useCallback } from 'react';
import { accountConsentIdentity } from '../utils/consentTick';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Links in the label point at the API for the generated PDF and at the site for
// the static pages.
const resolveHref = (href) => (href.startsWith('/api/') ? `${API_URL}${href}` : href);

export default function AccountConsentCheckbox({
  userId, token, checked, onChange, error, onReady,
  // 🔑 REPORTS WHICH DOCUMENTS, AT WHICH VERSIONS, THIS BOX COVERS. The form
  // stores a restorable tick against that identity, so a new version of Terms,
  // Privacy or the E-Sign Consent brings the box back EMPTY — a tick for one
  // version is not a tick for the next. See utils/consentTick.js.
  onDocumentIdentity,
}) {
  const [docs, setDocs] = useState(null);
  // ⚠️ NO `opening`, NO `awaitingReturn`, NO `openedRef` — those existed to tick
  // the box when the PDF viewer closed, which is the behaviour that was removed.
  //
  // 🔑 AND NO `openedAt` EITHER, AS OF 1 Oct 2026. The component tracked it to
  // decide whether to prompt the borrower to open the document; once the open
  // stopped being required, nothing in the UI turned on it. The SERVER still
  // records it when the link is tapped — this component simply has no reason to
  // know. Holding state nothing reads is how the next reader concludes the
  // requirement is still live.

  const auth = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

  useEffect(() => {
    let live = true;
    axios.get(`${API_URL}/api/account-consent/documents`, {
      params: { user_id: userId || undefined }, ...auth,
    })
      .then((r) => {
        if (!live) return;
        setDocs(r.data);
        if (onReady) onReady(!!r.data.esignReady);
        if (onDocumentIdentity) onDocumentIdentity(accountConsentIdentity(r.data.documents));
      })
      .catch(() => {
        if (!live) return;
        setDocs({ esignReady: false });
        if (onReady) onReady(false);
        // ⚠️ NO IDENTITY ON FAILURE, so nothing is restored. The box starting
        // empty is the safe outcome of not knowing which versions are current.
        if (onDocumentIdentity) onDocumentIdentity(null);
      });
    return () => { live = false; };
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 🔑 LOG THE OPEN, AND LET THE ANCHOR DO THE OPENING. The old version called
  // window.open inside the gesture to dodge the pop-up blocker; a plain
  // target="_blank" link was never subject to it, so the whole manoeuvre
  // — and the polling it needed — went away with the auto-open.
  //
  // ⚠️ FIRE-AND-FORGET, DELIBERATELY. If this POST fails the open is not
  // recorded and the server refuses at submit, which is the correct outcome:
  // the record route is the rule, and a client that could not log an open must
  // not be able to assert one.
  const logEsignOpen = useCallback(() => {
    axios.post(`${API_URL}/api/account-consent/document-open`, {
      user_id: userId || undefined, document_key: 'esign',
    }, auth)
      // ⚠️ THE RESPONSE IS NOT KEPT. The open is the server's record; nothing in
      // this component turns on it since the requirement was dropped, and a
      // failed log no longer blocks anything either.
      .catch(() => { /* the open is unrecorded for this file; nothing is gated on it */ });
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!docs || !docs.esignReady) return null;


  // 🚨 A PLAIN TOGGLE. No document opens, nothing is awaited, and nothing
  // ticks itself later.
  const onBoxTap = () => onChange(!checked);

  const parts = String(docs.label).split(/(Terms|Privacy Policy|E-Sign Consent)/g);
  const byLabel = Object.fromEntries(docs.documents.map((d) => [d.label, d]));
  const link = (d) => (
    <a
      key={d.key}
      href={resolveHref(d.href)}
      onClick={(e) => {
        // 🔑 THE LINK IS NOW THE ONLY THING THAT LOGS THE OPEN, and it no longer
        // preventDefaults — the anchor opens the document itself, in a new tab,
        // which is what it was always for. stopPropagation stays: the link sits
        // inside the <label>, and without it tapping the document name would
        // also toggle the box.
        e.stopPropagation();
        if (d.requiresOpen) logEsignOpen();
      }}
      target="_blank"
      rel="noopener noreferrer"
      className="underline"
      style={{ color: '#3a6ad9' }}
    >
      {d.label}
    </a>
  );

  return (
    <div
      className={`rounded-xl border p-4 transition-all duration-150 ${
        checked ? 'border-[#3a6ad9] bg-[#f5f7ff]'
          : error ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`}
    >
      <p className="text-sm font-semibold mb-3" style={{ color: '#1a1a1a' }}>
        Terms, Privacy Policy &amp; E-Sign Consent
      </p>
      <label className="flex items-start gap-2.5 cursor-pointer select-none">
        <button
          type="button"
          role="checkbox"
          aria-checked={checked}
          onClick={onBoxTap}
          className={`w-5 h-5 mt-0.5 rounded flex items-center justify-center flex-shrink-0 border-2 transition-all duration-150 ${
            checked ? 'border-[#3a6ad9] bg-[#3a6ad9]'
              : error ? 'border-red-400 bg-white'
                : 'border-gray-300 bg-white hover:border-[#3a6ad9]'}`}
        >
          {checked && (
            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </button>
        <span className="text-sm text-gray-700" style={{ lineHeight: '1.6' }}>
          {parts.map((p, i) => (byLabel[p] ? link(byLabel[p]) : <span key={i}>{p}</span>))}
        </span>
      </label>
      {/* 🚨 THE "PLEASE OPEN THE LINK BEFORE SUBMITTING" PROMPT IS GONE.
          It existed because the server refused a consent with no logged open,
          so a borrower could tick and then be stopped at submit. Jesse dropped
          that gate on 1 Oct 2026 — "making ticking sufficient means dropping
          that gate" — so the prompt would now be asking for something no longer
          required, which is worse than silence: it reads as a blocker and is
          not one.

          ⚠️ The remaining line is an INVITATION, not an instruction. The links
          are still there, still open the documents, and still log the open when
          tapped; nothing depends on the borrower doing it. */}
      {!checked && (
        <p className="text-xs text-gray-500 mt-2 ml-7">
          Tap any link above to read it. Tick the box to agree.
        </p>
      )}
      {error && <p className="text-xs text-red-500 mt-2 ml-7">{error}</p>}
    </div>
  );
}
